import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PHONE_DISPLAY, PHONE_TEL } from '../../lib/businessInfo';
import { prefetchChunk, prefetchRouteOnHover, prefetchRoutesOnTouch, auditInventoryPreloadHint } from '../../utils/routePrefetch';
import { blogPosts } from '../../mocks/blogPosts';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';
import { CORE_BUSINESS } from '../../lib/coreSchemas';

// ── Lazy-load MegaMenu — only loads when user opens a mega menu ──
const MegaMenu = lazy(() => import('../navigation/MegaMenu'));

// ── Prefetch function for MegaMenu chunk ──
const prefetchMegaMenu = () => prefetchChunk(() => import('../navigation/MegaMenu'));

// ── Shared event handlers for nav buttons ──
const navBtnHandlers = {
  onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
    (e.currentTarget as HTMLButtonElement).style.color = '#D97706';
  },
  onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>, isActive: boolean) => {
    if (!isActive) {
      (e.currentTarget as HTMLButtonElement).style.color = '#1C2B1E';
    }
    (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
  },
  onFocus: (e: React.FocusEvent<HTMLButtonElement>) => {
    (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 0 2px #D97706';
  },
  onBlur: (e: React.FocusEvent<HTMLButtonElement>) => {
    (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
  },
};

// ── Shared event handlers for dropdown/drawer links ──
const dropdownLinkHandlers = {
  onMouseEnter: (e: React.MouseEvent<HTMLAnchorElement>) => {
    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#F5F3EF';
    (e.currentTarget as HTMLAnchorElement).style.color = '#D97706';
  },
  onMouseLeave: (e: React.MouseEvent<HTMLAnchorElement>) => {
    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
    (e.currentTarget as HTMLAnchorElement).style.color = '#374151';
    (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
  },
  onFocus: (e: React.FocusEvent<HTMLAnchorElement>) => {
    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#F5F3EF';
    (e.currentTarget as HTMLAnchorElement).style.color = '#D97706';
    (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'inset 0 0 0 2px #D97706';
  },
  onBlur: (e: React.FocusEvent<HTMLAnchorElement>) => {
    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
    (e.currentTarget as HTMLAnchorElement).style.color = '#374151';
    (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
  },
};

// ── Shared event handlers for mobile drawer links (lighter background) ──
const drawerLinkHandlers = {
  onMouseEnter: (e: React.MouseEvent<HTMLAnchorElement>) => {
    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#F7F5F0';
    (e.currentTarget as HTMLAnchorElement).style.color = '#D97706';
  },
  onMouseLeave: (e: React.MouseEvent<HTMLAnchorElement>) => {
    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
    (e.currentTarget as HTMLAnchorElement).style.color = '#374151';
    (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
  },
  onFocus: (e: React.FocusEvent<HTMLAnchorElement>) => {
    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#F7F5F0';
    (e.currentTarget as HTMLAnchorElement).style.color = '#D97706';
    (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'inset 0 0 0 2px #D97706';
  },
  onBlur: (e: React.FocusEvent<HTMLAnchorElement>) => {
    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
    (e.currentTarget as HTMLAnchorElement).style.color = '#374151';
    (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
  },
};

// ── Shared event handlers for Contact and Blog direct links ──
const directLinkHandlers = {
  onMouseEnter: (e: React.MouseEvent<HTMLAnchorElement>) => {
    (e.currentTarget as HTMLAnchorElement).style.color = '#D97706';
  },
  onMouseLeave: (e: React.MouseEvent<HTMLAnchorElement>) => {
    (e.currentTarget as HTMLAnchorElement).style.color = '#1C2B1E';
    (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
  },
  onFocus: (e: React.FocusEvent<HTMLAnchorElement>) => {
    (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 0 2px #D97706';
  },
  onBlur: (e: React.FocusEvent<HTMLAnchorElement>) => {
    (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
  },
};

// ── Lightweight inventory count — deferred after first paint ──
// Uses a COUNT query only (no row data), loaded lazily so it never
// blocks the critical bundle or main-thread parse.
function useInventoryCount() {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { getSupabase } = await import('../../lib/supabase');
        const supabase = await getSupabase();
        const { count: n, error } = await supabase
          .from('vehicles')
          .select('id', { count: 'exact', head: true })
          .neq('status', 'sold')
          .neq('status', 'reconditioning')
          .neq('status', 'pending');
        if (!cancelled) {
          if (n !== null && !error) {
            setCount(n);
          } else {
            // Supabase unavailable — fall back to mock count so badge never shows 0
            const { MOCK_VEHICLES } = await import('../../mocks/mockVehicles');
            const mockCount = MOCK_VEHICLES.filter(
              (v) => v.status !== 'sold' && v.status !== 'reconditioning' && v.status !== 'pending'
            ).length;
            setCount(mockCount);
          }
        }
      } catch {
        // On any error, fall back to mock count
        if (!cancelled) {
          try {
            const { MOCK_VEHICLES } = await import('../../mocks/mockVehicles');
            const mockCount = MOCK_VEHICLES.filter(
              (v) => v.status !== 'sold' && v.status !== 'reconditioning' && v.status !== 'pending'
            ).length;
            setCount(mockCount);
          } catch {
            // Last resort — hide badge
          }
        }
      }
    };

    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(load, { timeout: 3000 });
      return () => { cancelled = true; cancelIdleCallback(id); };
    } else {
      const id = setTimeout(load, 500);
      return () => { cancelled = true; clearTimeout(id); };
    }
  }, []);

  return count;
}

// ── Lightweight blog article count — uses mock data ──
function useBlogCount() {
  return blogPosts.length;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface DropdownItem {
  label: string;
  path: string;
  icon: string;
}

interface DropdownGroup {
  id: string;
  label: string;
  items: DropdownItem[];
}

// Plain dropdowns only for Sell, Finance, About
const plainDropdowns: DropdownGroup[] = [
  {
    id: 'sell',
    label: 'Sell',
    items: [
      { label: 'Sell Your Car', path: '/sell-your-car', icon: 'ri-hand-coin-line' },
      { label: 'Trade-In', path: '/trade-in', icon: 'ri-exchange-line' },
      { label: 'Consignment', path: '/consignment', icon: 'ri-store-2-line' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    items: [
      { label: 'Financing Options', path: '/financing', icon: 'ri-bank-line' },
      { label: 'Apply for Financing', path: '/apply-for-financing', icon: 'ri-file-list-3-line' },
      { label: 'Broker Services', path: '/broker-services', icon: 'ri-user-star-line' },
    ],
  },
  {
    id: 'about',
    label: 'About',
    items: [
      { label: 'How It Works', path: '/how-it-works', icon: 'ri-route-line' },
      { label: 'Community Impact', path: '/community-impact', icon: 'ri-heart-2-line' },
      { label: 'Dog Tag Discount 🪖', path: '/military-discount', icon: 'ri-medal-line' },
      { label: 'Refer a Friend 🐾', path: '/referral', icon: 'ri-gift-2-line' },
      { label: 'The Guys Behind the Madness', path: '/about-the-owner', icon: 'ri-group-line' },
    ],
  },
];

// ── Map each dropdown group to its child routes for prefetching ──
const dropdownPrefetchRoutes: Record<string, string[]> = {
  sell:      ['/sell-your-car', '/trade-in', '/consignment'],
  finance:   ['/financing', '/apply-for-financing', '/broker-services'],
  about:     ['/how-it-works', '/reviews', '/community-impact', '/military-discount', '/referral', '/about-the-owner'],
};

// ── Map mobile drawer sections to their child routes for prefetching ──
const drawerSectionPrefetchRoutes: Record<string, string[]> = {
  buy:       ['/inventory', '/deal-radar', '/cars-under-10k', '/cars-under-30k', '/sold-vehicles'],
  sell:      ['/sell-your-car', '/trade-in', '/consignment'],
  finance:   ['/financing', '/apply-for-financing', '/broker-services'],
  about:     ['/how-it-works', '/reviews', '/community-impact', '/military-discount', '/referral', '/about-the-owner'],
  contact:   ['/contact', '/service-areas'],
  blog:      ['/blog', '/videos', '/faq', '/car-buying-guide'],
  resources: ['/reviews', '/faq', '/blog', '/car-buying-guide', '/contact', '/best-awd-cars-oregon-washington'],
};

// Mega menu triggers
type MegaMenuType = 'inventory' | 'services' | 'locations' | 'resources';

const megaMenuTriggers: { id: MegaMenuType; label: string }[] = [
  { id: 'inventory', label: 'Inventory' },
  { id: 'resources', label: 'Resources' },
];

interface NavbarProps {
  printHideNav?: boolean;
}

// ── SiteNavigationElement schema — injected once, describes full site nav ──
const SITE_NAV_ITEMS = [
  { name: 'Browse Inventory',        url: '/inventory' },
  { name: 'Deal Radar — First Dibs', url: '/deal-radar' },
  { name: 'Cars Under $10k',         url: '/cars-under-10k' },
  { name: 'Cars Under $30k',         url: '/cars-under-30k' },
  { name: 'Sold Vehicles',           url: '/sold-vehicles' },
  { name: 'Sell Your Car',           url: '/sell-your-car' },
  { name: 'Trade-In',                url: '/trade-in' },
  { name: 'Consignment',             url: '/consignment' },
  { name: 'Financing Options',       url: '/financing' },
  { name: 'Apply for Financing',     url: '/apply-for-financing' },
  { name: 'Broker Services',         url: '/broker-services' },
  { name: 'How It Works',            url: '/how-it-works' },
  { name: 'Compare Us',              url: '/compare' },
  { name: 'Car Buying Guide',        url: '/car-buying-guide' },
  { name: 'Customer Stories',        url: '/customer-stories' },
  { name: 'Reviews',                 url: '/reviews' },
  { name: 'Videos',                  url: '/videos' },
  { name: 'Blog',                    url: '/blog' },
  { name: 'FAQ',                     url: '/faq' },
  { name: 'Community Impact',        url: '/community-impact' },
  { name: 'Dog Tag Military Discount', url: '/military-discount' },
  { name: 'Refer a Friend Program',  url: '/referral' },
  { name: 'Service Areas',           url: '/service-areas' },
  { name: 'Contact',                 url: '/contact' },
];

function useSiteNavigationSchema() {
  useEffect(() => {
    injectJsonLd('schema-site-navigation', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      '@id': `${CORE_BUSINESS.url}/#site-navigation`,
      name: 'Rockhound Auto Site Navigation',
      description: 'Main navigation links for Rockhound Auto — used cars, financing, broker services, and more.',
      url: CORE_BUSINESS.url,
      numberOfItems: SITE_NAV_ITEMS.length,
      itemListElement: SITE_NAV_ITEMS.map((item, index) => ({
        '@type': 'SiteNavigationElement',
        position: index + 1,
        name: item.name,
        url: `${CORE_BUSINESS.url}${item.url}`,
      })),
    });

    return () => {
      removeJsonLd('schema-site-navigation');
    };
  }, []);
}

export default function Navbar({ printHideNav }: NavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [activeMegaMenu, setActiveMegaMenu] = useState<MegaMenuType | null>(null);
  const location = useLocation();
  const drawerRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const megaTriggerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const navRef = useRef<HTMLElement>(null);

  // Lightweight deferred count — replaces full useVehicles() call
  const inventoryCount = useInventoryCount();
  const showBadge = inventoryCount !== null && inventoryCount > 0;

  // Blog article count
  const blogCount = useBlogCount();

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setExpandedSection(null);
    setOpenDropdown(null);
    setActiveMegaMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  // Close plain dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isInsideAny = Object.values(dropdownRefs.current).some(
        (ref) => ref && ref.contains(target)
      );
      if (!isInsideAny) setOpenDropdown(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mega menu when clicking outside the nav
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isInsideNav = navRef.current?.contains(target);
      const isInsideTrigger = Object.values(megaTriggerRefs.current).some(
        (ref) => ref && ref.contains(target)
      );
      if (!isInsideNav && !isInsideTrigger) {
        setActiveMegaMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
        hamburgerRef.current?.focus();
      }
      if (e.key === 'Tab') {
        const drawer = drawerRef.current;
        if (!drawer) return;
        const focusable = drawer.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  // Close mega menu on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveMegaMenu(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleMegaTrigger = (id: MegaMenuType) => {
    setOpenDropdown(null);
    setActiveMegaMenu(activeMegaMenu === id ? null : id);
  };

  // ── Prefetch MegaMenu on hover/focus ──
  const handleMegaMenuPrefetch = () => {
    prefetchMegaMenu();
    // Also warm the inventory page chunk — user hovering "Buy" almost certainly
    // intends to navigate to /inventory next.
    prefetchRouteOnHover('/inventory');
    // Validate the /inventory hero preload hint (runs once per session)
    auditInventoryPreloadHint();
  };

  const navSections = [
    {
      id: 'buy',
      label: 'Buy',
      links: [
        { label: 'Browse Inventory', path: '/inventory', icon: 'ri-vip-crown-line' },
        { label: 'First Dibs', path: '/deal-radar', icon: 'ri-vip-crown-line' },
        { label: 'Cars Under $10k', path: '/cars-under-10k' },
        { label: 'Cars Under $30k', path: '/cars-under-30k' },
        { label: 'Sold Vehicles', path: '/sold-vehicles' },
      ],
    },
    {
      id: 'sell',
      label: 'Sell',
      links: [
        { label: 'Sell Your Car', path: '/sell-your-car' },
        { label: 'Trade-In', path: '/trade-in' },
        { label: 'Consignment', path: '/consignment' },
      ],
    },
    {
      id: 'finance',
      label: 'Finance',
      links: [
        { label: 'Financing Options', path: '/financing' },
        { label: 'Apply for Financing', path: '/apply-for-financing' },
        { label: 'Broker Services', path: '/broker-services' },
      ],
    },
    {
      id: 'about',
      label: 'About',
      links: [
        { label: 'How It Works', path: '/how-it-works' },
        { label: 'Reviews', path: '/reviews' },
        { label: 'Community Impact', path: '/community-impact' },
        { label: 'Dog Tag Discount 🪖', path: '/military-discount' },
        { label: 'Refer a Friend 🐾', path: '/referral' },
        { label: 'The Guys Behind the Madness', path: '/about-the-owner' },
      ],
    },
    {
      id: 'contact',
      label: 'Contact',
      links: [
        { label: 'Get in Touch', path: '/contact' },
        { label: 'Service Areas', path: '/service-areas' },
      ],
    },
    {
      id: 'blog',
      label: '2 Bald Guys Blog',
      links: [
        { label: 'All Articles', path: '/blog' },
        { label: 'Videos', path: '/videos' },
        { label: 'FAQ', path: '/faq' },
        { label: 'Car Buying Guide', path: '/car-buying-guide' },
      ],
    },
    {
      id: 'washington',
      label: '🗺️ Washington Buyers',
      links: [
        { label: 'WA Buyer FAQ', path: '/contact#washington-buyers' },
        { label: 'WA Cost Calculator', path: '/best-awd-cars-oregon-washington' },
        { label: 'SW Washington Delivery', path: '/used-cars-sw-washington' },
        { label: 'Long Beach / Ilwaco', path: '/used-cars-ilwaco-washington' },
      ],
    },
  ];

  if (printHideNav) return null;

  return (
    <>
      {/* ── NAVBAR ── */}
      <nav
        ref={navRef}
        aria-label="Main navigation"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          height: 'var(--nav-height)',
        }}
      >
        {/* White background shield — prevents hero overlay from bleeding through on mobile */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: '#ffffff',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: 'clamp(16px, 3vw, 40px)',
            paddingRight: 'clamp(16px, 3vw, 40px)',
          }}
        >
          {/* ── LEFT: Logo ── */}
          <Link
            to="/"
            aria-label="Rockhound Auto Sales — home"
            style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
          >
            <img
              alt="Rockhound Auto logo"
              width="102"
              height="80"
              fetchPriority="high"
              decoding="async"
              sizes="(max-width: 639px) 96px, 102px"
              srcSet="https://static.readdy.ai/image/b3efdc45e89a2660551e85a9fdfadec5/0b2475e2f7776f3579d13daea0959e21.webp?width=96&format=webp 96w, https://static.readdy.ai/image/b3efdc45e89a2660551e85a9fdfadec5/0b2475e2f7776f3579d13daea0959e21.webp?width=144&format=webp 144w, https://static.readdy.ai/image/b3efdc45e89a2660551e85a9fdfadec5/0b2475e2f7776f3579d13daea0959e21.webp?width=192&format=webp 192w, https://static.readdy.ai/image/b3efdc45e89a2660551e85a9fdfadec5/0b2475e2f7776f3579d13daea0959e21.webp?width=204&format=webp 204w"
              src="https://static.readdy.ai/image/b3efdc45e89a2660551e85a9fdfadec5/0b2475e2f7776f3579d13daea0959e21.webp?width=102&format=webp"
              className="block object-contain"
              style={{
                height: 'clamp(60px, 8vw, 80px)',
                width: 'auto',
                maxHeight: 'calc(var(--nav-height) - 8px)',
              }}
            />
          </Link>

          {/* ── CENTER: Desktop nav links ── */}
          <div
            className="hidden lg:flex items-center"
            style={{ gap: '2px' }}
            aria-label="Site sections"
          >
            {/* ── Mega Menu Trigger: Buy ── */}
            {megaMenuTriggers.map((trigger) => (
              <div
                key={trigger.id}
                style={{ position: 'relative' }}
                ref={(el) => { megaTriggerRefs.current[trigger.id] = el; }}
              >
                <button
                  aria-haspopup="dialog"
                  aria-expanded={activeMegaMenu === trigger.id}
                  onClick={() => handleMegaTrigger(trigger.id)}
                  onMouseEnter={(e) => {
                    navBtnHandlers.onMouseEnter(e);
                    if (trigger.id === 'inventory') {
                      handleMegaMenuPrefetch();
                    } else if (trigger.id === 'resources') {
                      prefetchMegaMenu();
                      (drawerSectionPrefetchRoutes.resources ?? []).forEach((route) =>
                        prefetchRouteOnHover(route)
                      );
                    }
                  }}
                  onMouseLeave={(e) => navBtnHandlers.onMouseLeave(e, activeMegaMenu === trigger.id)}
                  onFocus={(e) => {
                    navBtnHandlers.onFocus(e);
                    if (trigger.id === 'inventory') {
                      handleMegaMenuPrefetch();
                    } else if (trigger.id === 'resources') {
                      prefetchMegaMenu();
                    }
                  }}
                  onBlur={navBtnHandlers.onBlur}
                  onTouchStart={() => {
                    if (trigger.id === 'inventory') {
                      handleMegaMenuPrefetch();
                      prefetchRoutesOnTouch([
                        '/inventory',
                        '/deal-radar',
                        '/cars-under-10k',
                        '/cars-under-30k',
                        '/sold-vehicles',
                      ]);
                    } else if (trigger.id === 'resources') {
                      prefetchMegaMenu();
                      prefetchRoutesOnTouch(drawerSectionPrefetchRoutes.resources ?? []);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 14px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: activeMegaMenu === trigger.id ? '#D97706' : '#1C2B1E',
                    background: 'none',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'color 0.15s',
                    minHeight: 'unset',
                    minWidth: 'unset',
                    outline: 'none',
                  }}
                >
                  {trigger.label}
                  <span
                    style={{
                      fontSize: '12px',
                      transition: 'transform 0.2s',
                      transform: activeMegaMenu === trigger.id ? 'rotate(180deg)' : 'rotate(0deg)',
                      display: 'inline-block',
                      lineHeight: 1,
                    }}
                    aria-hidden="true"
                  >▾</span>
                  {/* Active underline indicator */}
                  {activeMegaMenu === trigger.id && (
                    <span
                      style={{
                        position: 'absolute',
                        bottom: '-2px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '20px',
                        height: '2px',
                        background: '#D97706',
                        borderRadius: '2px',
                      }}
                    />
                  )}
                </button>
              </div>
            ))}

            {/* ── Plain dropdowns: Sell, Finance, About ── */}
            {plainDropdowns.map((group) => (
              <div
                key={group.id}
                style={{ position: 'relative' }}
                ref={(el) => { dropdownRefs.current[group.id] = el; }}
              >
                <button
                  aria-haspopup="menu"
                  aria-expanded={openDropdown === group.id}
                  aria-controls={`desktop-menu-${group.id}`}
                  onClick={() => {
                    setActiveMegaMenu(null);
                    setOpenDropdown(openDropdown === group.id ? null : group.id);
                  }}
                  onMouseEnter={(e) => {
                    navBtnHandlers.onMouseEnter(e);
                    // Prefetch all child routes for this dropdown on hover
                    (dropdownPrefetchRoutes[group.id] ?? []).forEach((route) =>
                      prefetchRouteOnHover(route)
                    );
                  }}
                  onMouseLeave={(e) => navBtnHandlers.onMouseLeave(e, false)}
                  onFocus={navBtnHandlers.onFocus}
                  onBlur={navBtnHandlers.onBlur}
                  onTouchStart={() => {
                    // touchstart fires 50–100ms before mouseenter on iOS/Android tablets,
                    // giving extra lead time before the tap resolves into a navigation.
                    // Mirrors the same audit + prefetch that mouseenter already fires on desktop.
                    handleMegaMenuPrefetch();
                    prefetchRoutesOnTouch(dropdownPrefetchRoutes[group.id] ?? []);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 14px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#1C2B1E',
                    background: 'none',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'color 0.15s',
                    minHeight: 'unset',
                    minWidth: 'unset',
                    outline: 'none',
                  }}
                >
                  {group.label}
                  <span
                    style={{
                      fontSize: '12px',
                      transition: 'transform 0.2s',
                      transform: openDropdown === group.id ? 'rotate(180deg)' : 'rotate(0deg)',
                      display: 'inline-block',
                      lineHeight: 1,
                    }}
                    aria-hidden="true"
                  >▾</span>
                </button>

                {openDropdown === group.id && (
                  <ul
                    id={`desktop-menu-${group.id}`}
                    style={{
                      position: 'fixed',
                      top: 'var(--nav-height)',
                      width: '220px',
                      backgroundColor: '#ffffff',
                      borderRadius: '10px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                      border: '1px solid #f0f0f0',
                      zIndex: 99999,
                      padding: '6px 0',
                      listStyle: 'none',
                      margin: 0,
                    }}
                  >
                    {group.items.map((item) => (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          aria-current={location.pathname === item.path ? 'page' : undefined}
                          {...dropdownLinkHandlers}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 16px',
                            fontSize: '13.5px',
                            color: '#374151',
                            textDecoration: 'none',
                            transition: 'background 0.12s, color 0.12s',
                            outline: 'none',
                          }}
                        >
                          <span className="w-4 h-4 flex items-center justify-center" style={{ fontSize: '15px' }} aria-hidden="true">
                            <i className={item.icon}></i>
                          </span>
                          <span>{item.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {/* ── Contact — direct link ── */}
            <Link
              to="/contact"
              aria-current={location.pathname === '/contact' ? 'page' : undefined}
              onMouseEnter={(e) => {
                directLinkHandlers.onMouseEnter(e);
                prefetchRouteOnHover('/contact');
                prefetchRouteOnHover('/service-areas');
              }}
              onMouseLeave={directLinkHandlers.onMouseLeave}
              onFocus={directLinkHandlers.onFocus}
              onBlur={directLinkHandlers.onBlur}
              style={{
                padding: '8px 14px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#1C2B1E',
                textDecoration: 'none',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s',
                outline: 'none',
              }}
            >
              Contact
            </Link>

            {/* ── 2 Bald Guys Blog — direct link with badge ── */}
            <Link
              to="/blog"
              aria-current={location.pathname === '/blog' ? 'page' : undefined}
              onMouseEnter={(e) => {
                directLinkHandlers.onMouseEnter(e);
                prefetchRouteOnHover('/blog');
                prefetchRouteOnHover('/videos');
                prefetchRouteOnHover('/faq');
                prefetchRouteOnHover('/car-buying-guide');
              }}
              onMouseLeave={directLinkHandlers.onMouseLeave}
              onFocus={directLinkHandlers.onFocus}
              onBlur={directLinkHandlers.onBlur}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#1C2B1E',
                textDecoration: 'none',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s',
                outline: 'none',
              }}
            >
              <span className="w-4 h-4 flex items-center justify-center" style={{ fontSize: '14px' }} aria-hidden="true">
                <i className="ri-newspaper-line"></i>
              </span>
              2 Bald Guys Blog
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1px 7px',
                  backgroundColor: '#FEF3C7',
                  color: '#92400E',
                  fontSize: '11px',
                  fontWeight: 700,
                  borderRadius: '10px',
                  border: '1px solid #FDE68A',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
                aria-label={`${blogCount} articles`}
              >
                {blogCount}
              </span>
            </Link>
          </div>

          {/* ── RIGHT: Phone + Pill Badges + CTA + Hamburger ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <div className="hidden lg:block" style={{ width: '1px', height: '32px', backgroundColor: '#e5e7eb' }} aria-hidden="true" />

            {/*
              ── CLS FIX: "Browse Cars" button ──
              The inventory count badge pops in after the idle callback fires (~500ms),
              which previously caused the button to widen by ~30px and shift adjacent
              elements — a measurable CLS hit.

              Fix: always reserve the badge slot with a fixed min-width container.
              When count is null (loading), we render an invisible placeholder of the
              same dimensions so the button width never changes after first paint.
            */}
            <Link
              to="/inventory"
              aria-label="Browse our car inventory"
              className="hidden sm:inline-flex items-center"
              onMouseEnter={() => {
                prefetchRouteOnHover('/inventory');
                // Desktop parity — warm Contact + Blog routes from the drawer CTA too
                prefetchRouteOnHover('/contact');
                prefetchRouteOnHover('/service-areas');
                prefetchRouteOnHover('/blog');
                prefetchRouteOnHover('/videos');
                prefetchRouteOnHover('/faq');
                prefetchRouteOnHover('/car-buying-guide');
              }}
              style={{
                padding: '7px 18px',
                backgroundColor: '#1A2540',
                color: '#F5C842',
                fontWeight: 700,
                fontSize: '13px',
                borderRadius: '999px',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                border: '1.5px solid rgba(245,200,66,0.25)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
                transition: 'background 0.15s, border-color 0.15s',
                gap: '7px',
                letterSpacing: '0.01em',
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#1A2540';
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(245,200,66,0.25)';
              }}
            >
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                <i className="ri-car-line"></i>
              </span>
              Browse Cars
              {/*
                Badge slot — always rendered with a fixed min-width so the button
                never reflows. Invisible when count is still loading (null).
              */}
              <span
                aria-label={inventoryCount !== null ? `${inventoryCount} vehicles available` : undefined}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '28px',
                  padding: '1px 7px',
                  backgroundColor: inventoryCount !== null ? 'rgba(245,200,66,0.18)' : 'transparent',
                  color: '#F5C842',
                  fontSize: '11px',
                  fontWeight: 700,
                  borderRadius: '12px',
                  whiteSpace: 'nowrap',
                  border: inventoryCount !== null ? '1px solid rgba(245,200,66,0.30)' : '1px solid transparent',
                  // Smooth fade-in so the number doesn't "pop" visually
                  opacity: inventoryCount !== null ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                {inventoryCount ?? ''}
              </span>
            </Link>

            {/* ── Mobile: Browse Cars pill — visible without opening drawer ── */}
            <Link
              to="/inventory"
              aria-label="Browse our car inventory"
              className="flex lg:hidden items-center"
              style={{
                gap: '4px',
                padding: '6px 10px',
                backgroundColor: '#1A2540',
                color: '#F5C842',
                fontWeight: 700,
                fontSize: '12px',
                borderRadius: '999px',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                border: '1.5px solid rgba(245,200,66,0.25)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                letterSpacing: '0.01em',
              }}
            >
              <span className="w-3 h-3 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                <i className="ri-car-line"></i>
              </span>
              Browse Cars
              {/* Badge slot — always reserved to prevent layout shift */}
              <span
                aria-label={inventoryCount !== null ? `${inventoryCount} vehicles available` : undefined}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '22px',
                  padding: '1px 5px',
                  backgroundColor: inventoryCount !== null ? 'rgba(245,200,66,0.18)' : 'transparent',
                  color: '#F5C842',
                  fontSize: '10px',
                  fontWeight: 700,
                  borderRadius: '10px',
                  whiteSpace: 'nowrap',
                  border: inventoryCount !== null ? '1px solid rgba(245,200,66,0.30)' : '1px solid transparent',
                  opacity: inventoryCount !== null ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                {inventoryCount ?? ''}
              </span>
            </Link>

            <button
              ref={hamburgerRef}
              onClick={() => setIsMobileMenuOpen(prev => !prev)}
              aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-drawer"
              className="lg:hidden"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '44px',
                height: '44px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#374151',
                minHeight: 'unset',
                minWidth: 'unset',
                fontSize: '22px',
                lineHeight: 1,
              }}
            >
              <span className="w-6 h-6 flex items-center justify-center" aria-hidden="true">
                <i className={isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
              </span>
            </button>
          </div>
        </div>

        {/* ── MegaMenu panels — wrapped in Suspense for lazy loading ── */}
        {activeMegaMenu && (
          <Suspense fallback={<div style={{ height: '400px' }} />}>
            <MegaMenu
              isOpen={true}
              onClose={() => setActiveMegaMenu(null)}
              menuType={activeMegaMenu}
            />
          </Suspense>
        )}
      </nav>

      {/* ── Mobile Drawer Backdrop ── */}
      <div
        className="lg:hidden"
        onClick={() => setIsMobileMenuOpen(false)}
        onMouseEnter={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
        tabIndex={-1}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 60,
          opacity: isMobileMenuOpen ? 1 : 0,
          pointerEvents: isMobileMenuOpen ? 'auto' : 'none',
          transition: 'opacity 0.3s',
        }}
      />

      {/* ── Mobile Drawer ── */}
      <div
        ref={drawerRef}
        id="mobile-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className="lg:hidden"
        onMouseLeave={() => setIsMobileMenuOpen(false)}
        style={{
          position: 'fixed',
          top: 'var(--nav-height)',
          right: 0,
          height: 'calc(100% - var(--nav-height))',
          width: '280px',
          backgroundColor: '#ffffff',
          zIndex: 70,
          transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.16,1,0.3,1)',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Drawer Nav */}
        <nav aria-label="Mobile navigation" style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>

          {/* ── Phone strip at top of drawer ── */}
          <a
            href={`tel:${PHONE_TEL}`}
            aria-label={`Call Rockhound Auto at ${PHONE_DISPLAY}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 12px',
              marginBottom: '8px',
              backgroundColor: '#F0FDF4',
              border: '1px solid #BBF7D0',
              borderRadius: '8px',
              textDecoration: 'none',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '30px',
                height: '30px',
                backgroundColor: '#166534',
                borderRadius: '50%',
                flexShrink: 0,
                fontSize: '14px',
              }}
              aria-hidden="true"
            >
              <i className="ri-phone-line" style={{ color: '#ffffff' }}></i>
            </span>
            <div style={{ lineHeight: 1.3, minWidth: 0 }}>
              <div style={{ fontSize: '9px', fontWeight: 600, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                Call Anytime
              </div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>
                {PHONE_DISPLAY}
              </div>
            </div>
          </a>

          {navSections.map((section) => (
            <div key={section.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <button
                onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                onMouseEnter={() => {
                  // Warm up all child routes the moment the user's finger/cursor
                  // touches the accordion button — fires before the section opens
                  (drawerSectionPrefetchRoutes[section.id] ?? []).forEach((route) =>
                    prefetchRouteOnHover(route)
                  );
                }}
                onTouchStart={() => {
                  // touchstart fires 50–100ms before mouseenter on iOS/Android,
                  // giving extra lead time before the tap resolves into a navigation.
                  // Shares the same deduplication set so routes are never fetched twice.
                  prefetchRoutesOnTouch(drawerSectionPrefetchRoutes[section.id] ?? []);
                }}
                aria-expanded={expandedSection === section.id}
                aria-controls={`mobile-section-${section.id}`}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 4px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  minHeight: 'unset',
                  minWidth: 'unset',
                }}
              >
                <span style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>{section.label}</span>
                <span
                  aria-hidden="true"
                  style={{
                    fontSize: '18px',
                    color: '#6b7280',
                    transition: 'transform 0.25s',
                    transform: expandedSection === section.id ? 'rotate(180deg)' : 'rotate(0deg)',
                    display: 'inline-block',
                    lineHeight: 1,
                  }}
                >▾</span>
              </button>
              <div
                id={`mobile-section-${section.id}`}
                style={{
                  overflow: 'hidden',
                  maxHeight: expandedSection === section.id ? `${section.links.length * 56}px` : '0px',
                  transition: 'max-height 0.3s ease',
                }}
              >
                <ul style={{ listStyle: 'none', margin: 0, padding: '0 0 8px 0' }}>
                  {section.links.map((link) => (
                    <li key={link.path}>
                      <Link
                        to={link.path}
                        aria-current={location.pathname === link.path ? 'page' : undefined}
                        onClick={() => setIsMobileMenuOpen(false)}
                        {...drawerLinkHandlers}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 12px',
                          minHeight: '44px',
                          fontSize: '14px',
                          color: '#374151',
                          textDecoration: 'none',
                          borderRadius: '6px',
                          transition: 'background 0.12s, color 0.12s',
                          outline: 'none',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {link.path === '/deal-radar' && (
                            <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
                              <i className="ri-vip-crown-line" style={{ color: '#D97706' }}></i>
                            </span>
                          )}
                          <span>{link.label}</span>
                        </span>
                        {link.path === '/blog' && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '1px 7px',
                              backgroundColor: '#FEF3C7',
                              color: '#92400E',
                              fontSize: '11px',
                              fontWeight: 700,
                              borderRadius: '10px',
                              border: '1px solid #FDE68A',
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                            }}
                          >
                            {blogCount}
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </nav>

        {/* Drawer Footer */}
        <div style={{ padding: '16px', borderTop: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>

          {/* ── Refer a Friend + Donation banner links ── */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <Link
              to="/referral"
              onClick={() => setIsMobileMenuOpen(false)}
              style={{
                flex: '1 1 120px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '12px 8px',
                minHeight: '44px',
                backgroundColor: '#FEF3C7',
                color: '#92400E',
                borderRadius: '8px',
                fontSize: '12.5px',
                fontWeight: 700,
                textDecoration: 'none',
                border: '1px solid #FDE68A',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                <i className="ri-gift-line"></i>
              </span>
              Refer a Friend
            </Link>
            <Link
              to="/community-impact"
              onClick={() => setIsMobileMenuOpen(false)}
              style={{
                flex: '1 1 120px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '12px 8px',
                minHeight: '44px',
                backgroundColor: '#F0FDF4',
                color: '#166534',
                borderRadius: '8px',
                fontSize: '12.5px',
                fontWeight: 700,
                textDecoration: 'none',
                border: '1px solid #BBF7D0',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                <i className="ri-heart-line"></i>
              </span>
              $100 Donation
            </Link>
          </div>

          <Link
            to="/inventory"
            aria-label="Browse our car inventory"
            onClick={() => setIsMobileMenuOpen(false)}
            onMouseEnter={() => {
              prefetchRouteOnHover('/inventory');
              // Desktop parity — warm Contact + Blog routes from the drawer CTA too
              prefetchRouteOnHover('/contact');
              prefetchRouteOnHover('/service-areas');
              prefetchRouteOnHover('/blog');
              prefetchRouteOnHover('/videos');
              prefetchRouteOnHover('/faq');
              prefetchRouteOnHover('/car-buying-guide');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: '100%',
              backgroundColor: '#1A2540',
              color: '#F5C842',
              padding: '12px 16px',
              borderRadius: '999px',
              fontSize: '14px',
              fontWeight: 700,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              border: '1.5px solid rgba(245,200,66,0.25)',
              boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
            }}
          >
            <span className="w-4 h-4 flex items-center justify-center" style={{ color: '#F5C842' }} aria-hidden="true">
              <i className="ri-car-line"></i>
            </span>
            Browse Cars
            {showBadge && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1px 7px',
                  backgroundColor: 'rgba(245,200,66,0.18)',
                  color: '#F5C842',
                  fontSize: '11px',
                  fontWeight: 700,
                  borderRadius: '12px',
                  whiteSpace: 'nowrap',
                  border: '1px solid rgba(245,200,66,0.30)',
                }}
                aria-label={`${inventoryCount} vehicles available`}
              >
                {inventoryCount}
              </span>
            )}
          </Link>
        </div>
      </div>
    </>
  );
}